import { Descope, AuthProvider } from '@descope/react-sdk/flows';
import StatsigDualInstanceProvider from '@linktr.ee/feature-flags-mfe/StatsigDualInstanceProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthLoadingShell,
  LoadingShell,
} from '@shared/components/LoadingShell';
import i18n, { detectStatsigLocale, getCompoundLocale } from '@shared/i18n';
import {
  getAuthOtpChallengeRequestFailureReasonCode,
  publishAuthOtpChallengeRequestCompletedEvent,
  publishLoginAttemptedEvent,
  type AuthFlow,
  type AuthOtpChannel,
  type AuthOtpRequestTrigger,
} from '@shared/utils/analytics/events';
import {
  readAuthDesignDecision,
  writeAuthDesignDecision,
} from '@shared/utils/authDesignDecision';
import type { AuthErrorMessage } from '@shared/utils/authErrorMessage';
import { getAuthErrorMessage } from '@shared/utils/authErrorMessage';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import {
  getMobileStableId,
  isMobileApp,
  isMobileSmsAuthEnabled,
  isSmsSignupEnabled,
  isWebSmsAuthEnabled,
} from '@shared/utils/descopeQueryParams';
import { getBrowserId } from '@shared/utils/getBrowserId';
import getPlatformType from '@shared/utils/getPlatformType';
import {
  getOnboardingVariant,
  isRandomizableTreatmentOnboardingSession,
} from '@shared/utils/newRegisterDesign';
import { normaliseRoute } from '@shared/utils/normaliseRoute';
import {
  SMS_AUTH_ALLOWED_COUNTRIES,
  type PhoneCountryCode,
} from '@shared/utils/phone';
import { prefetchNextHop } from '@shared/utils/prefetchNextHop';
import {
  reportClientError,
  setClientErrorContext,
} from '@shared/utils/reportClientError';
import {
  extractLoginId,
  isConfirmedDisabledError,
  isOAuthCanceledByUserError,
  isPotentiallyDisabledUserError,
  redirectIfSuspended,
  redirectToSuspended,
} from '@shared/utils/suspendedUserRedirect';
import {
  AUTH_SCREEN_READY_EVENT,
  isAuthScreenReady,
  useAuthScreenReady,
} from '@shared/utils/useAuthScreenReady';
import { useIsProfileReferral } from '@shared/utils/useIsProfileReferral';
import { getArkoseGateConfig } from './arkoseGateConfig';
import {
  isReturningUserOtpScreen,
  shouldUseNewOtpVerificationScreen,
} from './designDecision';
import {
  isEmailOtpVerificationScreen,
  isLoginOtpVerificationScreen,
  isProtectedEmailConfirmationScreen,
  isSignupOtpVerificationScreen,
  isUniversalLoginCustomScreen,
} from './flowContract';
import {
  isNewRegisterSessionRandomizable,
  useNewRegisterDesignLayer,
  type NewRegisterDesignStatus,
} from './newRegisterDesignLayer';
import {
  getSignupGateState,
  isOptimisticSignupVisible,
  useResolveFlowReady,
  useOptimisticSignupProps,
} from './optimisticSignup';
import { usePaidMediaSignupVariant } from './paidMediaSignupExperiment';
import { isQaPasskeysEnabled } from './passkeys';
import { usePendingSignupEmail, useRegisterHold } from './registerHold';
import { PASSKEY_INTERACTION_IDS, ScreenNames } from './screenMapping';
import { useLocalizationModeSync } from './useLocalizationModeSync';
import { ADD_MFA_WITH_SMS_OTP_FLOW_ID } from '../add-mfa-with-sms-otp/screenMapping';
import { ADD_MFA_WITH_TOTP_FLOW_ID } from '../add-mfa-with-totp/screenMapping';
import {
  getArkoseGateVerifyOrBypassAuthFlow,
  routeArkoseGateVerifyOrBypassScreen,
} from '../arkose-gate-verify-or-bypass/routeScreen';
import { isArkoseGateVerifyOrBypassScreen } from '../arkose-gate-verify-or-bypass/screenMapping';
import { FORGOT_USERNAME_FLOW_ID } from '../forgot-username/screenMapping';
import { GUEST_PROMOTION_FLOW_ID } from '../guest-promotion/screenMapping';
import { routeMfaChallengeScreen } from '../mfa-challenge/routeScreen';
import { MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID } from '../mobile-consumer-promotion-set-email/screenMapping';
import { PHONE_SMS_OTP_AUTH_INTERACTION_IDS } from '../phone-sms-otp-auth/interactions';
import { routePhoneSmsOtpAuthScreen } from '../phone-sms-otp-auth/routeScreen';
import { PhoneSmsOtpAuthScreenNames } from '../phone-sms-otp-auth/screenMapping';
import { REQUEST_PASSWORD_RESET_FLOW_ID } from '../request-password-reset/screenMapping';
import { RESET_PASSWORD_FLOW_ID } from '../reset-password/screenMapping';
import {
  RESET_SMS_MFA_FLOW_ID,
  RESET_TOTP_MFA_FLOW_ID,
} from '../reset-sms-mfa/screenMapping';
import bannerLoginImage from './images/banner-login-desktop.webp';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import type {
  DescopeFormData,
  DescopeScreenContext,
  DescopeScreenError,
} from './types/descope';
import '@linktr.ee/design-tokens/dist/web/variables.css';
// Arbor's font CSS ships Link Sans faces with same-origin bundled woff2 URLs.
import '@linktr.ee/arbor/fonts.css';
import '@shared/style.css';

const isOptimisticSignupEntry = (): boolean => {
  const isMobileAppSession = isMobileApp();
  return (
    normaliseRoute(window.location.hash) === '/register' &&
    isRandomizableTreatmentOnboardingSession(
      window.location.search,
      window.location.hash,
      isMobileAppSession
    )
  );
};

let shouldRetryNewRegisterScreen = false;
const retryNewRegisterScreen = () =>
  // @ts-expect-error Vite emits query-suffixed imports as distinct module URLs.
  import('./screens/NewRegisterScreen?retry') as Promise<
    typeof import('./screens/NewRegisterScreen')
  >;
const loadNewRegisterScreen = () =>
  shouldRetryNewRegisterScreen
    ? retryNewRegisterScreen()
    : import('./screens/NewRegisterScreen');
const NewRegisterScreen = React.lazy(loadNewRegisterScreen);
const PasskeyEnrollmentScreen = React.lazy(
  () => import('./screens/PasskeyEnrollmentScreen')
);

if (isOptimisticSignupEntry()) {
  void loadNewRegisterScreen().catch(error => {
    shouldRetryNewRegisterScreen = true;
    reportClientError('Optimistic signup screen prefetch failed', error, {
      flowId: UNIVERSAL_LOGIN_FLOW_ID,
      source: 'optimistic-signup-screen-prefetch',
    });
  });
}

const isSignupContinuationScreen = (
  screenName: string | undefined,
  hasPendingSignupEmail: boolean,
  smsAuthFlow: AuthFlow = 'login',
  context?: DescopeScreenContext,
  form?: DescopeFormData,
  isSignupArkoseGateReported = false
): boolean =>
  // Render callers classify the current state; callback callers also pass the
  // previously reported gate when the screen transition is not yet rendered.
  isSignupOtpVerificationScreen(screenName) ||
  (hasPendingSignupEmail && isLoginOtpVerificationScreen(screenName)) ||
  (smsAuthFlow === 'signup' &&
    (screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN ||
      screenName === PhoneSmsOtpAuthScreenNames.VERIFY_SMS_OTP)) ||
  screenName === ScreenNames.CHOOSE_USERNAME_EMAIL_OTP ||
  screenName === ScreenNames.SOCIAL_SIGNUP_USERNAME ||
  isSignupArkoseGateReported ||
  isSignupArkoseGate(
    screenName,
    context,
    form,
    smsAuthFlow,
    hasPendingSignupEmail
  );

const isSignupScreenName = (screenName?: string): boolean =>
  screenName === ScreenNames.SIGNUP_PAGE;

const isRegisterHoldLoading = (
  requestPending: boolean,
  errorText?: string
): boolean => requestPending && !errorText;

type SignupActionIntent = 'email' | 'phone' | 'google' | 'apple';

const SignupScreenGate: React.FC<{
  isPending: boolean;
  isEnabled: boolean;
  newScreen: React.ReactNode;
  controlScreen: React.ReactNode;
}> = ({ isPending, isEnabled, newScreen, controlScreen }) => {
  if (isPending) {
    return isAuthScreenReady() ? <LoadingShell /> : null;
  }

  return isEnabled ? newScreen : controlScreen;
};

const AuthAwareLoadingFallback: React.FC = () => {
  const [screenReady, setScreenReady] = useState(isAuthScreenReady);

  useEffect(() => {
    if (isAuthScreenReady()) {
      setScreenReady(true);
      return undefined;
    }

    const handleScreenReady = (): void => {
      setScreenReady(true);
    };
    window.addEventListener(AUTH_SCREEN_READY_EVENT, handleScreenReady, {
      once: true,
    });

    return () =>
      window.removeEventListener(AUTH_SCREEN_READY_EVENT, handleScreenReady);
  }, []);

  return screenReady ? <LoadingShell /> : null;
};

const LoadedNewRegisterScreen: React.FC<{
  props: React.ComponentProps<typeof NewRegisterScreen>;
}> = ({ props }) => {
  useAuthScreenReady(ScreenNames.SIGNUP_PAGE);
  return <NewRegisterScreen {...props} />;
};

const getAuthScreenReadyName = (
  screenName: string | undefined,
  isGatePending: boolean,
  isNewRegisterDesign: boolean
): string | undefined =>
  screenName === ScreenNames.SIGNUP_PAGE &&
  (isGatePending || isNewRegisterDesign)
    ? undefined
    : screenName;

const NewRegisterScreenGate: React.FC<{
  props: React.ComponentProps<typeof NewRegisterScreen>;
}> = ({ props }) => (
  <React.Suspense fallback={<AuthAwareLoadingFallback />}>
    <LoadedNewRegisterScreen props={props} />
  </React.Suspense>
);

const HeldNewRegisterScreens: React.FC<{
  sms: { show: boolean; props: React.ComponentProps<typeof NewRegisterScreen> };
  email: {
    show: boolean;
    props: React.ComponentProps<typeof NewRegisterScreen>;
  };
  arkose: {
    show: boolean;
    props: React.ComponentProps<typeof NewRegisterScreen>;
  };
}> = ({ sms, email, arkose }) => (
  <>
    {sms.show && <NewRegisterScreenGate props={sms.props} />}
    {email.show && <NewRegisterScreenGate props={email.props} />}
    {arkose.show && <NewRegisterScreenGate props={arkose.props} />}
  </>
);

export const ArkoseGateScreens: React.FC<{
  screenName?: string;
  authError: AuthErrorMessage;
  loginHref: string;
  newDesign: boolean;
  next?: DescopeNext;
  arkoseGateForm: DescopeFormData;
  smsAuthForm: DescopeFormData;
  shouldHoldSignup: boolean;
}> = ({
  screenName,
  authError,
  loginHref,
  newDesign,
  next,
  arkoseGateForm,
  smsAuthForm,
  shouldHoldSignup,
}) => {
  const config = getArkoseGateConfig(screenName, arkoseGateForm, smsAuthForm);
  if (!config) {
    return null;
  }
  const content = (
    <ArkoseGateScreen
      authFlow={config.authFlow}
      newDesign={config.authFlow === 'signup' ? newDesign : undefined}
      backHref={config.backHref}
      // Login-gate back targets are already absolute URLs; signup-gate recovery
      // changes destination, unlike guest-claim's deliberate WebView handoff.
      loginHref={loginHref}
      onContinue={async () => {
        await next?.(config.interactionId, config.form);
      }}
      authError={authError}
    />
  );
  return config.authFlow === 'signup' ? (
    <div
      className={
        shouldHoldSignup
          ? 'pointer-events-none fixed inset-0 opacity-0'
          : undefined
      }
      aria-hidden={shouldHoldSignup}
    >
      {content}
    </div>
  ) : (
    content
  );
};

const DESCOPE_PROJECT_ID =
  import.meta.env.VITE_DESCOPE_PROJECT_ID || 'P32ACVpudk8MNftmpf1LR1pW5k8s';
const DESCOPE_BASE_URL =
  import.meta.env.VITE_DESCOPE_BASE_URL || 'https://ciam.linktr.ee';
const DEFAULT_STATSIG_STAGE = 'production';
const STATSIG_SDK_KEY =
  import.meta.env.VITE_STATSIG_SDK_KEY ||
  'client-J1tag9olmqMG8MoC8GFkRX3e8gX43Gyx3nfJaysnVpi';
const getStatsigStage = (): string =>
  import.meta.env.VITE_STATSIG_STAGE || DEFAULT_STATSIG_STAGE;
const LINKTREE_BASE_URL =
  import.meta.env.VITE_LINKTREE_BASE_URL || 'https://linktr.ee';
const UNIVERSAL_LOGIN_FLOW_ID = 'universal-login';
const GUEST_PROMOTION_ROUTE = '/guest-claim';
const GUEST_PROMOTION_FLOWS_ROUTE = '/flows/guest-promotion';
const MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE =
  '/mobile-consumer-promotion-set-email';
const MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE =
  '/flows/mobile-consumer-promotion-set-email';
const ADD_MFA_WITH_SMS_OTP_ROUTE = '/add-mfa-with-sms-otp';
const ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE = '/flows/add-mfa-with-sms-otp';
const ADD_MFA_WITH_TOTP_ROUTE = '/add-mfa-with-totp';
const ADD_MFA_WITH_TOTP_FLOWS_ROUTE = '/flows/add-mfa-with-totp';
const RESET_SMS_MFA_ROUTE = '/reset-sms-mfa';
const RESET_SMS_MFA_FLOWS_ROUTE = '/flows/reset-sms-mfa';
const RESET_TOTP_MFA_ROUTE = '/reset-totp-mfa';
const RESET_TOTP_MFA_FLOWS_ROUTE = '/flows/reset-totp-mfa';
const REQUEST_PASSWORD_RESET_ROUTE = '/request';
const REQUEST_PASSWORD_RESET_FLOWS_ROUTE = '/flows/request-password-reset';
const FORGOT_USERNAME_ROUTE = '/forgot-username';
const FORGOT_USERNAME_FLOWS_ROUTE = '/flows/forgot-username';
const RESET_PASSWORD_ROUTE_PREFIX = '/reset/';
const RESET_PASSWORD_FLOWS_ROUTE_PREFIX = '/flows/reset-password/';
const RESET_PASSWORD_ROUTE = '/reset';
const RESET_PASSWORD_FLOWS_ROUTE = '/flows/reset-password';
const SMS_AUTH_FLOW_FORM_KEY = 'form.smsAuthFlow';
const DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE = 'OTPSignUpOrInEmailFailed';
const PROTECTED_EMAIL_CONFIRMATION_SUBMIT_INTERACTION_ID = 'L4SwjB0Qpa';
const AUTH_IDENTIFIER_VALIDATION_PURPOSE_KEY = 'form.validationPurpose';
const AUTH_IDENTIFIER_CONTINUE_VALIDATION_PURPOSE = 'identifier_continue';
const EMAIL_OTP_FALLBACK_VALIDATION_PURPOSE = 'email_otp_fallback';
const DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST = [
  'freeTrial',
  'freeTrialEntryPoint',
  'frequency',
  'tier',
  'couponId',
  'addons',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'onboardingVariant',
  'passkeys',
] as const;

const AddMfaWithSmsOtpFlow = React.lazy(
  () => import('../add-mfa-with-sms-otp/App')
);
const AddMfaWithTotpFlow = React.lazy(() => import('../add-mfa-with-totp/App'));
const ForgotUsernameFlow = React.lazy(() => import('../forgot-username/App'));
const GuestPromotionFlow = React.lazy(() => import('../guest-promotion/App'));
const MobileConsumerPromotionSetEmailFlow = React.lazy(
  () => import('../mobile-consumer-promotion-set-email/App')
);
const RequestPasswordResetFlow = React.lazy(
  () => import('../request-password-reset/App')
);
const ResetPasswordFlow = React.lazy(() => import('../reset-password/App'));
const ResetSmsMfaFlow = React.lazy(() => import('../reset-sms-mfa/App'));
const ResetTotpMfaFlow = React.lazy(() =>
  import('../reset-sms-mfa/App').then(module => ({
    default: module.ResetTotpMfaFlow,
  }))
);
let shouldRetryDeferredAuthScreens = false;
const retryDeferredAuthScreens = () =>
  // @ts-expect-error Vite emits query-suffixed imports as distinct module URLs.
  import('./screens/deferred?retry') as Promise<
    typeof import('./screens/deferred')
  >;
const loadDeferredAuthScreens = () =>
  shouldRetryDeferredAuthScreens
    ? retryDeferredAuthScreens()
    : import('./screens/deferred');
const ArkoseGateScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.ArkoseGateScreen,
  }))
);
const OTPVerificationScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.OTPVerificationScreen,
  }))
);
const NewOTPVerificationScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.NewOTPVerificationScreen,
  }))
);
const PasswordLoginScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.PasswordLoginScreen,
  }))
);
const PhoneLoginScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.PhoneLoginScreen,
  }))
);
const ProtectedEmailConfirmationScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.ProtectedEmailConfirmationScreen,
  }))
);
const UsernameScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.UsernameScreen,
  }))
);
const NewUsernameScreen = React.lazy(() =>
  loadDeferredAuthScreens().then(module => ({
    default: module.NewUsernameScreen,
  }))
);
const DEFERRED_AUTH_SCREENS_PREFETCH_DELAY_MS = 1000;

const renderAuthScreenTransitionBoundary = (
  isPending: boolean,
  authScreen: React.ReactElement
): React.ReactElement => (
  <div className="contents" aria-busy={isPending} inert={isPending}>
    {authScreen}
  </div>
);

const useSignupScreenComponents = (
  newDesignEnabled: boolean,
  screenName?: string
) => {
  const isSignupOtpVerification = isSignupOtpVerificationScreen(screenName);
  const isReturningSignupOtp = isReturningUserOtpScreen(screenName);
  const shouldUseNewOtp = shouldUseNewOtpVerificationScreen(
    newDesignEnabled,
    screenName
  );

  return {
    isSignupOtpVerification,
    isReturningSignupOtp,
    OtpVerificationScreen: shouldUseNewOtp
      ? NewOTPVerificationScreen
      : OTPVerificationScreen,
    UsernameScreenComponent:
      newDesignEnabled === true ? NewUsernameScreen : UsernameScreen,
  };
};

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeSdkNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type UniversalLoginFlowState = DescopeScreenContext & {
  screenName?: string;
  error?: DescopeScreenError;
  next?: DescopeNext;
  lastAuth?: { loginId?: string };
};

interface DescopeBridge {
  abortFlow?: (reason?: string) => void;
}

const getDescopeBridge = (): DescopeBridge | undefined =>
  (window as Window & { descopeBridge?: DescopeBridge }).descopeBridge;

const persistAuthDesignDecision = (
  screenName: string | undefined,
  status: NewRegisterDesignStatus,
  enabled: boolean
): void => {
  if (status === 'pending') {
    return;
  }

  if (screenName === ScreenNames.SIGNUP_PAGE) {
    writeAuthDesignDecision(enabled);
  } else if (screenName === ScreenNames.LOGIN_PAGE) {
    // Arkose host screens must not write this marker: page screens establish
    // the current auth design before their nested flow runs, and writing here
    // would clear a returning-user decision after the Login page guard.
    writeAuthDesignDecision(false);
  }
};

const withValidationPurpose = (
  payload: Record<string, unknown>,
  validationPurpose: string
): Record<string, unknown> => ({
  ...payload,
  [AUTH_IDENTIFIER_VALIDATION_PURPOSE_KEY]: validationPurpose,
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const toDescopeScreenError = (error: unknown): DescopeScreenError =>
  isRecord(error) ? error : { detail: error };

const isSmsSignupDeliveryFallback = (errorText?: string): boolean =>
  Boolean(errorText && /^Failed to sign up or in(?::|$)/u.test(errorText));

const isSignupArkoseGate = (
  screenName: string | undefined,
  context?: DescopeScreenContext,
  form?: DescopeFormData,
  smsAuthFlow: AuthFlow = 'login',
  hasPendingSignupEmail = false
): boolean =>
  screenName === ScreenNames.ARKOSE_SIGNUP_GATE ||
  screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE ||
  (Boolean(screenName && isArkoseGateVerifyOrBypassScreen(screenName)) &&
    (smsAuthFlow === 'signup' ||
      hasPendingSignupEmail ||
      getArkoseGateVerifyOrBypassAuthFlow(context, form) === 'signup'));

const getRecordField = (
  record: Record<string, unknown>,
  field: string
): Record<string, unknown> | undefined => {
  const value = record[field];
  return isRecord(value) ? value : undefined;
};

const getStringField = (
  record: Record<string, unknown>,
  field: string
): string | undefined => {
  const value = record[field];
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
};

const getBooleanField = (
  record: Record<string, unknown>,
  field: string
): boolean | undefined => {
  const value = record[field];
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === 'true') {
      return true;
    }
    if (normalizedValue === 'false') {
      return false;
    }
  }

  return undefined;
};

const getNumberField = (
  record: Record<string, unknown>,
  field: string
): number | undefined => {
  const value = record[field];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

const getProtectedMaskedEmailFromContext = (
  context: unknown
): string | undefined => {
  if (!isRecord(context)) {
    return undefined;
  }

  const connectors = getRecordField(context, 'connectors');
  const httpResult = connectors
    ? getRecordField(connectors, 'httpResult')
    : undefined;

  return (
    (httpResult ? getStringField(httpResult, 'maskedEmail') : undefined) ||
    getStringField(context, 'maskedEmail')
  );
};

const getProtectedEmailCooldownFromContext = (
  context: unknown
): { rateLimited?: boolean; retryAfterSeconds?: number } => {
  if (!isRecord(context)) {
    return {};
  }

  const connectors = getRecordField(context, 'connectors');
  const protectedEmailVerificationResult = connectors
    ? getRecordField(connectors, 'protectedEmailVerificationResult')
    : undefined;
  const source = protectedEmailVerificationResult || context;

  return {
    rateLimited:
      getBooleanField(source, 'rateLimited') ??
      getBooleanField(source, 'isRateLimited'),
    retryAfterSeconds:
      getNumberField(source, 'retryAfterSeconds') ??
      getNumberField(source, 'retryAfter') ??
      getNumberField(source, 'retryAfterSecs'),
  };
};

const isEmailOtpChallengeFailure = (
  error: unknown
): error is DescopeScreenError & {
  type: typeof DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE;
} => isRecord(error) && error.type === DESCOPE_EMAIL_OTP_CHALLENGE_FAILURE_TYPE;

const getPreVerificationOtpFailureAuthFlow = (
  screenName: string,
  context?: Record<string, unknown>,
  form?: Record<string, unknown>
): AuthFlow | undefined => {
  if (isArkoseGateVerifyOrBypassScreen(screenName)) {
    return getArkoseGateVerifyOrBypassAuthFlow(context, form);
  }

  if (
    screenName === ScreenNames.SIGNUP_PAGE ||
    screenName === ScreenNames.ARKOSE_SIGNUP_GATE ||
    screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE
  ) {
    return 'signup';
  }

  if (
    screenName === ScreenNames.LOGIN_PAGE ||
    screenName === ScreenNames.PASSWORD_OTP_LOGIN ||
    screenName === ScreenNames.ARKOSE_LOGIN_GATE ||
    screenName === ScreenNames.ARKOSE_SMS_LOGIN_GATE
  ) {
    return 'login';
  }

  return undefined;
};

const getInitialOtpFailureSignature = (
  authFlow: AuthFlow,
  otpChannel: AuthOtpChannel,
  screenName: string,
  error: unknown
): string => {
  const errorType = isRecord(error) ? error.type : undefined;
  const errorText = isRecord(error) ? error.text : undefined;

  return [
    authFlow,
    otpChannel,
    screenName,
    typeof errorType === 'string' ? errorType : '',
    typeof errorText === 'string' ? errorText : '',
  ].join('|');
};

const omitSmsAuthFlow = (
  value: Record<string, unknown>
): Record<string, unknown> => {
  const nextValue = { ...value };
  delete nextValue[SMS_AUTH_FLOW_FORM_KEY];
  delete nextValue.smsAuthFlow;
  return nextValue;
};

const omitSmsPhone = (
  value: Record<string, unknown>
): Record<string, unknown> => {
  const nextValue = omitSmsAuthFlow(value);
  delete nextValue.phone;
  return nextValue;
};

// Module scope on purpose: `UniversalLoginFlow` is at the eslint complexity
// ceiling (max 64), so the conditional lives out here rather than inline.
const withSmsPhone = (
  value: Record<string, unknown>,
  phone: string | undefined
): Record<string, unknown> => (phone ? { ...value, phone } : value);

const SIGNUP_MODE_SCREENS: string[] = [
  ScreenNames.SIGNUP_PAGE,
  ScreenNames.ARKOSE_SIGNUP_GATE,
  ScreenNames.ARKOSE_SMS_SIGNUP_GATE,
  ScreenNames.VERIFY_OTP_SIGNUP,
  ScreenNames.CHOOSE_USERNAME_EMAIL_OTP,
  ScreenNames.SOCIAL_SIGNUP_USERNAME,
];

const isSignupModeScreen = (
  screenName: string | undefined,
  context?: DescopeScreenContext,
  form?: DescopeFormData,
  smsAuthFlow: AuthFlow = 'login',
  hasPendingSignupEmail = false
): boolean =>
  Boolean(screenName && SIGNUP_MODE_SCREENS.includes(screenName)) ||
  isSignupArkoseGate(
    screenName,
    context,
    form,
    smsAuthFlow,
    hasPendingSignupEmail
  );

// Monolith path the signup flow lands on after a successful auth.
// Prefetching it collapses next-hop TTFB to edge-POP RTT (MONEY-792).
//
// Only the signup landing is prefetched. The login landing (`/admin`) is
// auth-gated by the monolith firewall, so a prefetch issued from the
// (logged-out) login screen is denied and 302'd to `/login`. That denied
// request overwrites the firewall's saved `target_path` with `/admin`,
// discarding the user's original deep link (e.g. `/admin/settings`) and
// dropping them on the bare dashboard post-login (APP-1631). It also yields
// no perf benefit, since the redirect (not the admin HTML) is what gets
// cached. Do NOT add an `/admin` prefetch here.
const SIGNUP_REDIRECT_PATH = '/register/get-started';

const isMobileConsumerPromotionSetEmailRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE ||
    hashRoute === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE ||
    pathRoute.endsWith(MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOWS_ROUTE) ||
    pathRoute.endsWith(MOBILE_CONSUMER_PROMOTION_SET_EMAIL_ROUTE)
  );
};

const isGuestPromotionRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === GUEST_PROMOTION_ROUTE ||
    hashRoute === GUEST_PROMOTION_FLOWS_ROUTE ||
    pathRoute.endsWith(GUEST_PROMOTION_ROUTE) ||
    pathRoute.endsWith(GUEST_PROMOTION_FLOWS_ROUTE)
  );
};

const isAddMfaWithSmsOtpRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === ADD_MFA_WITH_SMS_OTP_ROUTE ||
    hashRoute === ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE ||
    pathRoute.endsWith(ADD_MFA_WITH_SMS_OTP_ROUTE) ||
    pathRoute.endsWith(ADD_MFA_WITH_SMS_OTP_FLOWS_ROUTE)
  );
};

const isAddMfaWithTotpRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === ADD_MFA_WITH_TOTP_ROUTE ||
    hashRoute === ADD_MFA_WITH_TOTP_FLOWS_ROUTE ||
    pathRoute.endsWith(ADD_MFA_WITH_TOTP_ROUTE) ||
    pathRoute.endsWith(ADD_MFA_WITH_TOTP_FLOWS_ROUTE)
  );
};

const isResetSmsMfaRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === RESET_SMS_MFA_ROUTE ||
    hashRoute === RESET_SMS_MFA_FLOWS_ROUTE ||
    pathRoute.endsWith(RESET_SMS_MFA_ROUTE) ||
    pathRoute.endsWith(RESET_SMS_MFA_FLOWS_ROUTE)
  );
};

const isResetTotpMfaRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === RESET_TOTP_MFA_ROUTE ||
    hashRoute === RESET_TOTP_MFA_FLOWS_ROUTE ||
    pathRoute.endsWith(RESET_TOTP_MFA_ROUTE) ||
    pathRoute.endsWith(RESET_TOTP_MFA_FLOWS_ROUTE)
  );
};

const isRequestPasswordResetRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === REQUEST_PASSWORD_RESET_ROUTE ||
    hashRoute === REQUEST_PASSWORD_RESET_FLOWS_ROUTE ||
    pathRoute.endsWith(REQUEST_PASSWORD_RESET_ROUTE) ||
    pathRoute.endsWith(REQUEST_PASSWORD_RESET_FLOWS_ROUTE)
  );
};

const isForgotUsernameRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);

  return (
    hashRoute === FORGOT_USERNAME_ROUTE ||
    hashRoute === FORGOT_USERNAME_FLOWS_ROUTE ||
    pathRoute.endsWith(FORGOT_USERNAME_ROUTE) ||
    pathRoute.endsWith(FORGOT_USERNAME_FLOWS_ROUTE)
  );
};

const getRouteToken = (route: string, prefix: string): string | undefined => {
  const routeIndex = route.lastIndexOf(prefix);

  if (routeIndex === -1) {
    return undefined;
  }

  const token = route.slice(routeIndex + prefix.length).split('/')[0];

  if (!token) {
    return undefined;
  }

  try {
    return decodeURIComponent(token);
  } catch {
    return token;
  }
};

const getResetPasswordTokenFromRoute = (): string | undefined => {
  const routes = [
    normaliseRoute(window.location.hash),
    normaliseRoute(window.location.pathname),
  ];

  for (const route of routes) {
    const token =
      getRouteToken(route, RESET_PASSWORD_ROUTE_PREFIX) ??
      getRouteToken(route, RESET_PASSWORD_FLOWS_ROUTE_PREFIX);

    if (token) {
      return token;
    }
  }

  return undefined;
};

const isResetPasswordRoute = (): boolean => {
  const hashRoute = normaliseRoute(window.location.hash);
  const pathRoute = normaliseRoute(window.location.pathname);
  const routes = [hashRoute, pathRoute];

  return routes.some(
    route =>
      route === RESET_PASSWORD_ROUTE ||
      route === RESET_PASSWORD_FLOWS_ROUTE ||
      route.includes(RESET_PASSWORD_ROUTE_PREFIX) ||
      route.includes(RESET_PASSWORD_FLOWS_ROUTE_PREFIX)
  );
};

const getInitialFlowId = (): string =>
  isResetPasswordRoute()
    ? RESET_PASSWORD_FLOW_ID
    : isForgotUsernameRoute()
      ? FORGOT_USERNAME_FLOW_ID
      : isRequestPasswordResetRoute()
        ? REQUEST_PASSWORD_RESET_FLOW_ID
        : isResetTotpMfaRoute()
          ? RESET_TOTP_MFA_FLOW_ID
          : isResetSmsMfaRoute()
            ? RESET_SMS_MFA_FLOW_ID
            : isAddMfaWithTotpRoute()
              ? ADD_MFA_WITH_TOTP_FLOW_ID
              : isAddMfaWithSmsOtpRoute()
                ? ADD_MFA_WITH_SMS_OTP_FLOW_ID
                : isGuestPromotionRoute()
                  ? GUEST_PROMOTION_FLOW_ID
                  : isMobileConsumerPromotionSetEmailRoute()
                    ? MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID
                    : UNIVERSAL_LOGIN_FLOW_ID;

/**
 * Promotes the paid-media signup params from the hash into the real query
 * string so they survive the Descope auth hop, preserve UTM attribution, and
 * continue into onboarding.
 */
const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);

  // Native flows need the hash route and params after Descope redirects back.
  if (isMobileApp()) {
    return url.toString();
  }

  const hashRoute = normaliseRoute(url.hash);

  if (hashRoute === '/register') {
    const hashQueryString = url.hash.split('?')[1];

    if (hashQueryString) {
      const hashSearchParams = new URLSearchParams(hashQueryString);

      DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST.forEach(paramName => {
        const paramValue = hashSearchParams.get(paramName);

        if (paramValue !== null && !url.searchParams.has(paramName)) {
          url.searchParams.set(paramName, paramValue);
        }
      });
    }
  }

  url.hash = '';
  return url.toString();
};

/**
 * Collects whitelisted auth params from the current URL so the login fallback
 * can preserve paid-media and UTM attribution across the Descope hop.
 */
const getWhitelistedDescopeQueryParams = (): URLSearchParams => {
  const whitelistedQueryParams = new URLSearchParams();
  const searchParams = new URLSearchParams(window.location.search);
  const hashQueryString =
    normaliseRoute(window.location.hash) === '/register'
      ? window.location.hash.split('?')[1]
      : undefined;
  const hashParams = hashQueryString
    ? new URLSearchParams(hashQueryString)
    : undefined;

  DESCOPE_REGISTER_HASH_QUERY_PARAM_WHITELIST.forEach(paramName => {
    const paramValue =
      searchParams.get(paramName) ?? hashParams?.get(paramName);

    if (paramValue !== undefined && paramValue !== null) {
      whitelistedQueryParams.set(paramName, paramValue);
    }
  });

  return whitelistedQueryParams;
};

const HostedDescopeFlow: React.FC<{ flowId: string }> = ({ flowId }) => {
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const [screenName, setScreenName] = useState<string>();
  useAuthScreenReady(screenName);

  useEffect(() => {
    setClientErrorContext({ flowId });
  }, [flowId]);

  return (
    <Descope
      flowId={flowId}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId({})}
      onScreenUpdate={(nextScreenName: string) => {
        setClientErrorContext({ flowId, screenName: nextScreenName });
        setScreenName(nextScreenName);
        return false;
      }}
    />
  );
};

const UniversalLoginFlow: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<UniversalLoginFlowState>({ error: {} });
  const authError = getAuthErrorMessage(state?.error?.text, t);
  const displayErrorText = authError.message;
  const [isScreenTransitionPending, startScreenTransition] =
    React.useTransition();
  const currentScreenName = state.screenName;
  const showPaidMediaSignupVariant =
    usePaidMediaSignupVariant(currentScreenName);
  const [form, setForm] = useState<DescopeFormData>({});
  const [descopeFlowResetKey, setDescopeFlowResetKey] = useState(0);
  const [optimisticSignupEntry] = useState(isOptimisticSignupEntry);
  const latestScreenRef = useRef<string | undefined>(undefined);
  const latestNextRef = useRef<DescopeNext | undefined>(undefined);
  const flowReadyPromiseRef = useRef<Promise<void> | undefined>(undefined);
  const resolveFlowReadyRef = useRef<(() => void) | undefined>(undefined);

  if (!flowReadyPromiseRef.current) {
    flowReadyPromiseRef.current = new Promise(resolve => {
      resolveFlowReadyRef.current = resolve;
    });
  }
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [whitelistedRedirectQueryParams] = useState(
    getWhitelistedDescopeQueryParams
  );
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const skipNextInitialOtpChallengeEventsRef = useRef(false);
  const hasSeenOtpVerificationScreenRef = useRef(false);
  const previousReportedScreenNameRef = useRef<string | undefined>(undefined);
  const lastReportedScreenNameRef = useRef<string | undefined>(undefined);
  const previousReportedSignupArkoseGateRef = useRef(false);
  const lastReportedSignupArkoseGateRef = useRef(false);
  const lastInitialOtpChallengeFailureSignatureRef = useRef<string | undefined>(
    undefined
  );
  // TRU-3217: onScreenUpdate re-fires whenever React re-renders the Descope
  // shell after a screen-update event, and the disabled-error detection
  // (OTPSignUpOrInEmailFailed) is intentionally broad — it fires for every
  // OTP/email login failure, not just real suspensions. Without a guard we'd
  // re-hit the monolith on every re-render of the same error state.
  //
  // Tracks loginIds we've *already asked the monolith about* for the lifetime
  // of this component (not just in-flight requests). Once a check has been
  // dispatched for a given loginId we don't ask again — even if the call
  // resolved with suspended:false (the common case) and the same error context
  // fires again on the next re-render. The user can still trigger a fresh
  // check by typing a different identifier (different key) or refreshing the
  // page (component remount → fresh ref).
  const suspensionCheckedRef = useRef<Set<string>>(new Set());
  const [prefilledUsername] = useState<string | null>(() => {
    const hash = window.location.hash;
    // Expected Hash format: #/register?username={username}
    const queryString = hash.split('?')[1];
    if (queryString) {
      const searchParams = new URLSearchParams(queryString);
      const usernameParam = searchParams.get('username');
      if (usernameParam) {
        return usernameParam;
      }
    }
    return null;
  });
  const [onboardingVariant] = useState(() =>
    getOnboardingVariant(window.location.search, window.location.hash)
  );
  const isProfileReferral = useIsProfileReferral();
  const [newRegisterVariant, setNewRegisterVariant] = useState<string | null>(
    onboardingVariant
  );
  const [smsAuthEligibility] = useState(() => {
    const isMobileAppSession = isMobileApp();
    const isWebSmsEnabled = isWebSmsAuthEnabled();
    const isSmsSignupEnabledForQa = isSmsSignupEnabled();
    return {
      isMobileAppSession,
      isSmsSignupEnabled: isSmsSignupEnabledForQa,
      showSmsLogin: isMobileSmsAuthEnabled() || isWebSmsEnabled,
    };
  });
  const [showPasskeyLogin] = useState(isQaPasskeysEnabled);
  const { isMobileAppSession, isSmsSignupEnabled: isSmsSignupEnabledForQa } =
    smsAuthEligibility;
  // Mobile-app sessions are the only sessions excluded from randomization.
  const isSessionRandomizable = isNewRegisterSessionRandomizable(
    smsAuthEligibility.isMobileAppSession
  );
  // Both the login and signup pages nest the same `phone-sms-otp-auth` sub-flow,
  // so its screen names cannot tell the two entry points apart. Remember which
  // button started the SMS leg to keep the copy and analytics on that side.
  const [smsAuthFlow, setSmsAuthFlow] = useState<AuthFlow>('login');
  const smsAuthFlowRef = useRef<AuthFlow>('login');
  const pendingSignupEmailState = usePendingSignupEmail();
  const {
    pendingSignupEmail,
    setPendingSignupEmail,
    getHasPendingSignupEmail,
  } = pendingSignupEmailState;
  const newRegisterDesignStatus = useNewRegisterDesignLayer(
    {
      isRegisterScreen: currentScreenName === ScreenNames.SIGNUP_PAGE,
      isSignupContinuationScreen: isSignupContinuationScreen(
        currentScreenName,
        pendingSignupEmailState.hasPendingSignupEmail,
        smsAuthFlow,
        state,
        form,
        false
      ),
    },
    isSessionRandomizable,
    onboardingVariant,
    setNewRegisterVariant
  );
  const isNewRegisterGatePending = newRegisterDesignStatus === 'pending';
  const newRegisterDesignEnabled = newRegisterDesignStatus === 'treatment';
  useEffect(() => {
    const returningUserBackToLogin =
      currentScreenName === ScreenNames.LOGIN_PAGE &&
      isLoginOtpVerificationScreen(previousReportedScreenNameRef.current) &&
      readAuthDesignDecision();

    if (!returningUserBackToLogin) {
      persistAuthDesignDecision(
        currentScreenName,
        newRegisterDesignStatus,
        newRegisterDesignEnabled
      );
    }
  }, [currentScreenName, newRegisterDesignEnabled, newRegisterDesignStatus]);
  const showSmsLogin = smsAuthEligibility.showSmsLogin;
  const platformType = getPlatformType(isProfileReferral, isMobileAppSession);
  const emailOtpLoginAttemptPublishedRef = useRef(false);
  const [smsPhone, setSmsPhone] = useState<string | undefined>();
  const [pendingSmsCountry, setPendingSmsCountry] =
    useState<PhoneCountryCode>();
  const formWithoutSmsAuthFlow = omitSmsAuthFlow(form);
  const smsAuthForm = {
    ...withSmsPhone(
      { ...(state?.form ?? {}), ...formWithoutSmsAuthFlow },
      smsPhone
    ),
    [SMS_AUTH_FLOW_FORM_KEY]: smsAuthFlow,
  };
  const smsScreenContext = {
    ...state,
    form: smsAuthForm,
  };
  // The nested gate on a signup SMS leg continues with this payload rather than
  // `smsAuthForm` (that branch keys off the flow-reported `form.smsAuthFlow`,
  // which must not be overwritten by our local copy), so the phone rides along
  // here too — the SMS send on the far side of the gate needs it, and Descope's
  // echoed `context.form` is not relied on for it anywhere else. `smsPhone` is
  // cleared on back, so a phone never leaks into an email-path gate.
  const arkoseGateForm = withSmsPhone(
    {
      ...(isRecord(state?.form) ? state.form : {}),
      ...formWithoutSmsAuthFlow,
    },
    smsPhone
  );
  const isSignupMode = isSignupModeScreen(
    state.screenName,
    state,
    arkoseGateForm,
    smsAuthFlow,
    pendingSignupEmailState.hasPendingSignupEmail
  );
  const {
    isSignupOtpVerification,
    isReturningSignupOtp,
    OtpVerificationScreen,
    UsernameScreenComponent,
  } = useSignupScreenComponents(newRegisterDesignEnabled, state?.screenName);
  const isLoginOtpVerification = isLoginOtpVerificationScreen(
    state?.screenName
  );
  useAuthScreenReady(
    getAuthScreenReadyName(
      currentScreenName,
      isNewRegisterGatePending,
      newRegisterDesignEnabled
    )
  );

  useEffect(() => {
    if (
      currentScreenName !== ScreenNames.LOGIN_PAGE &&
      currentScreenName !== ScreenNames.SIGNUP_PAGE
    ) {
      return undefined;
    }

    const prefetchDeferredAuthScreens = (): void => {
      void loadDeferredAuthScreens().catch(error => {
        // Browsers cache a rejected module import for its URL. Switch the
        // eventual screen load to a separately emitted URL so a transient
        // idle-prefetch failure cannot strand the active auth journey.
        shouldRetryDeferredAuthScreens = true;
        reportClientError('Deferred auth screens prefetch failed', error, {
          flowId: UNIVERSAL_LOGIN_FLOW_ID,
          screenName: currentScreenName,
          source: 'deferred-auth-screens-prefetch',
        });
      });
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleCallbackId = window.requestIdleCallback(
        prefetchDeferredAuthScreens,
        { timeout: DEFERRED_AUTH_SCREENS_PREFETCH_DELAY_MS }
      );

      return () => window.cancelIdleCallback(idleCallbackId);
    }

    const prefetchTimer = window.setTimeout(
      prefetchDeferredAuthScreens,
      DEFERRED_AUTH_SCREENS_PREFETCH_DELAY_MS
    );

    return () => window.clearTimeout(prefetchTimer);
  }, [currentScreenName]);

  const resetEmailOtpLoginAttempt = useCallback(() => {
    emailOtpLoginAttemptPublishedRef.current = false;
  }, []);

  const markEmailOtpLoginAttemptPublished = useCallback(() => {
    emailOtpLoginAttemptPublishedRef.current = true;
  }, []);

  const publishEmailOtpLoginAttemptOnce = useCallback(() => {
    if (emailOtpLoginAttemptPublishedRef.current) {
      return;
    }

    emailOtpLoginAttemptPublishedRef.current = true;
    publishLoginAttemptedEvent('Email OTP', platformType);
  }, [platformType]);

  const markExplicitOtpChallengeRequested = useCallback(() => {
    skipNextInitialOtpChallengeEventsRef.current = true;
  }, []);

  const resetExplicitOtpChallengeRequest = useCallback(() => {
    skipNextInitialOtpChallengeEventsRef.current = false;
  }, []);

  const resetDescopeError = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      error: {},
    }));
  }, []);

  const updateForm = useCallback((nextForm: DescopeFormData) => {
    setForm(nextForm);
  }, []);

  const resetInitialOtpChallengeFailureReporting = useCallback(() => {
    lastInitialOtpChallengeFailureSignatureRef.current = undefined;
  }, []);

  const resetFlowTracking = useCallback(() => {
    hasSeenOtpVerificationScreenRef.current = false;
    resetInitialOtpChallengeFailureReporting();
    resetExplicitOtpChallengeRequest();
    resetEmailOtpLoginAttempt();
  }, [
    resetEmailOtpLoginAttempt,
    resetExplicitOtpChallengeRequest,
    resetInitialOtpChallengeFailureReporting,
  ]);

  const consumeInitialOtpChallengeRequestTrigger = useCallback(():
    AuthOtpRequestTrigger | undefined => {
    if (skipNextInitialOtpChallengeEventsRef.current) {
      skipNextInitialOtpChallengeEventsRef.current = false;
      return undefined;
    }

    return 'initial_route';
  }, []);

  useEffect(() => {
    const screenName = state.screenName;
    if (!screenName) {
      return;
    }
    if (isSignupMode) {
      prefetchNextHop(SIGNUP_REDIRECT_PATH);
    }
  }, [isSignupMode, state.screenName]);

  const isSignupArkoseScreen = isSignupArkoseGate(
    state.screenName,
    state,
    arkoseGateForm,
    smsAuthFlow,
    pendingSignupEmailState.hasPendingSignupEmail
  );
  const registerHold = useRegisterHold({
    enabled: newRegisterDesignEnabled,
    authFlow: smsAuthFlow,
    isSignupContext:
      state.screenName === ScreenNames.SIGNUP_PAGE || isSignupArkoseScreen,
    isPhoneScreen:
      state.screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN,
    isSignupPage: state.screenName === ScreenNames.SIGNUP_PAGE,
    isArkoseScreen: isSignupArkoseScreen,
    hasPendingSignupEmail: pendingSignupEmailState.hasPendingSignupEmail,
    pendingSignupEmail: pendingSignupEmailState.pendingSignupEmail,
    setPendingSignupEmail: pendingSignupEmailState.setPendingSignupEmail,
    errorText: state.error?.text,
  });
  const {
    setAutoSubmitPendingSmsPhone,
    setAutoSubmitPendingSignupEmail,
    setPhoneSubmitInFlight,
    setPendingSmsPhone,
  } = registerHold;
  const resetSignupLegState = useCallback(() => {
    setPendingSignupEmail(undefined);
    setSmsAuthFlow('login');
    smsAuthFlowRef.current = 'login';
    setSmsPhone(undefined);
    setPendingSmsPhone(undefined);
    setPendingSmsCountry(undefined);
    setAutoSubmitPendingSmsPhone(false);
    setPhoneSubmitInFlight(false);
    setAutoSubmitPendingSignupEmail(false);
  }, [
    setAutoSubmitPendingSmsPhone,
    setAutoSubmitPendingSignupEmail,
    setPendingSignupEmail,
    setPendingSmsCountry,
    setPendingSmsPhone,
    setPhoneSubmitInFlight,
    setSmsPhone,
  ]);
  const resetMobileSmsLoginFlow = useCallback((): boolean => {
    if (!isMobileAppSession) {
      return false;
    }

    const bridge = getDescopeBridge();
    if (!bridge?.abortFlow) {
      return false;
    }

    resetSignupLegState();
    setForm({});
    setState({ error: {} });
    setDescopeFlowResetKey(resetKey => resetKey + 1);
    resetFlowTracking();
    bridge.abortFlow();

    return true;
  }, [isMobileAppSession, resetFlowTracking, resetSignupLegState]);
  const replaySignupActionRef = useRef<SignupActionIntent | undefined>(
    undefined
  );
  const replayPhoneSubmitRef = useRef<
    { phone: string; country?: PhoneCountryCode } | undefined
  >(undefined);
  const timedOutSignupSubmitRef = useRef<
    | { intent: 'email' }
    | {
        intent: 'phone';
        phone: { phone: string; country?: PhoneCountryCode };
      }
    | undefined
  >(undefined);
  const signupActionHandlersRef = useRef<
    Partial<Record<SignupActionIntent, () => Promise<void>>>
  >({});
  const runSignupAction = async (
    intent: SignupActionIntent,
    action: () => Promise<void>
  ): Promise<void> => {
    const timedOutSubmit = timedOutSignupSubmitRef.current;
    if (timedOutSubmit) {
      replaySignupActionRef.current = intent;
      timedOutSignupSubmitRef.current = undefined;
      restartSignupFlow();
      return;
    }
    if (
      state.screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN &&
      state.next
    ) {
      replaySignupActionRef.current = intent;
      restartSignupFlow();
      return;
    }
    await action();
  };
  const handleSignupPhoneSubmit = async (
    phone: string,
    country?: PhoneCountryCode
  ): Promise<void> => {
    const timedOutSubmit = timedOutSignupSubmitRef.current;
    if (timedOutSubmit) {
      replaySignupActionRef.current = 'phone';
      replayPhoneSubmitRef.current = {
        phone,
        country:
          country ??
          (timedOutSubmit.intent === 'phone'
            ? timedOutSubmit.phone.country
            : undefined),
      };
      timedOutSignupSubmitRef.current = undefined;
      restartSignupFlow();
      return;
    }
    if (state.next) {
      const isPhoneScreen =
        state.screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN;
      const nextForm = isPhoneScreen
        ? { ...smsAuthForm, phone }
        : {
            ...formWithoutSmsAuthFlow,
            phone,
            [SMS_AUTH_FLOW_FORM_KEY]: 'signup',
          };
      setSmsAuthFlow('signup');
      smsAuthFlowRef.current = 'signup';
      registerHold.setPendingSmsPhone(phone);
      setPhoneSubmitInFlight(true);
      setPendingSmsCountry(country);
      setPendingSignupEmail(undefined);
      setAutoSubmitPendingSmsPhone(!isPhoneScreen);
      setForm(formWithoutSmsAuthFlow);
      await state.next(
        isPhoneScreen
          ? PHONE_SMS_OTP_AUTH_INTERACTION_IDS.SUBMIT_PHONE
          : 'KNjXbdgxdn',
        nextForm
      );
    }
  };
  const signupSharedProps = {
    onChange: (data: DescopeFormData) =>
      setForm(prev => ({ ...prev, ...data })),
    onClickContinue: async () => {
      await runSignupAction('email', async () => {
        if (state.next) {
          registerHold.setPendingSmsPhone(undefined);
          setPendingSmsCountry(undefined);
          setPendingSignupEmail(
            typeof form.email === 'string'
              ? form.email
              : typeof state.form?.email === 'string'
                ? state.form.email
                : undefined
          );
          setAutoSubmitPendingSignupEmail(true);
          resetInitialOtpChallengeFailureReporting();
          await state.next('c6ffx0tFb9', form);
        }
      });
    },
    onClickGoogle: async () => {
      // Social signup stays on the signup leg; retain its auth context.
      registerHold.setPendingSmsPhone(undefined);
      setPendingSmsCountry(undefined);
      setAutoSubmitPendingSmsPhone(false);
      await runSignupAction('google', async () => {
        setPendingSignupEmail(undefined);
        setAutoSubmitPendingSignupEmail(false);
        if (state.next) {
          await state.next('e6emQLX4gJ', { provider: 'google', ...form });
        }
      });
    },
    onClickApple: async () => {
      // Social signup stays on the signup leg; retain its auth context.
      registerHold.setPendingSmsPhone(undefined);
      setPendingSmsCountry(undefined);
      setAutoSubmitPendingSmsPhone(false);
      await runSignupAction('apple', async () => {
        setPendingSignupEmail(undefined);
        setAutoSubmitPendingSignupEmail(false);
        if (state.next) {
          await state.next('ThET2KRZVy', { provider: 'apple', ...form });
        }
      });
    },
    onClickSMS: handleSignupPhoneSubmit,
    onClickClosePhone: async () => {
      if (state.screenName !== PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN) {
        return;
      }
      restartSignupFlow();
    },
    newRegisterVariant,
    isSmsSignupEnabled: isSmsSignupEnabledForQa,
    resetError: () =>
      setState(prevState => ({
        ...prevState,
        error: {},
      })),
    errorText: displayErrorText,
    context: {
      ...state,
      form: {
        ...(state.form ?? {}),
        ...form,
      },
    },
  };
  signupActionHandlersRef.current = {
    email: signupSharedProps.onClickContinue,
    phone: async () => {
      const replayPhone = replayPhoneSubmitRef.current;
      replayPhoneSubmitRef.current = undefined;
      if (replayPhone) {
        await signupSharedProps.onClickSMS?.(
          replayPhone.phone,
          replayPhone.country
        );
      }
    },
    google: signupSharedProps.onClickGoogle,
    apple: signupSharedProps.onClickApple,
  };
  useEffect(() => {
    if (
      state.screenName !== ScreenNames.SIGNUP_PAGE ||
      !replaySignupActionRef.current
    ) {
      return;
    }
    const intent = replaySignupActionRef.current;
    replaySignupActionRef.current = undefined;
    void signupActionHandlersRef.current[intent]?.().catch(error => {
      const replayError = toDescopeScreenError(error);
      reportClientError('Deferred signup action failed', error, {
        flowId: UNIVERSAL_LOGIN_FLOW_ID,
        screenName: ScreenNames.SIGNUP_PAGE,
        source: 'signup-action-replay',
        intent,
      });
      setAutoSubmitPendingSmsPhone(false);
      setAutoSubmitPendingSignupEmail(false);
      setState(prevState => ({
        ...prevState,
        error: {
          ...replayError,
          text:
            typeof replayError.text === 'string' && replayError.text
              ? replayError.text
              : i18n.t('Something went wrong'),
        },
      }));
    });
  }, [
    setAutoSubmitPendingSignupEmail,
    setAutoSubmitPendingSmsPhone,
    state.screenName,
  ]);
  const showOptimisticSignup = isOptimisticSignupVisible(
    optimisticSignupEntry,
    state.screenName
  );
  const signupGateState = getSignupGateState(
    isSignupScreenName(state.screenName),
    showOptimisticSignup,
    isNewRegisterGatePending,
    newRegisterDesignEnabled
  );
  const {
    shouldRenderSmsRegister,
    shouldHoldSmsRegister,
    shouldHoldEmailRegister,
    shouldHoldArkoseRegister,
    pendingSmsPhone,
    autoSubmitPendingSmsPhone,
    phoneSubmitInFlight,
    autoSubmitPendingSignupEmail,
  } = registerHold;
  const releaseSignupHold = useCallback(
    (timeoutType: 'hold' | 'submit-email' | 'submit-phone' = 'hold') => {
      const isSubmitTimeout = timeoutType !== 'hold';
      const message = isSubmitTimeout
        ? 'Signup register submit timed out'
        : 'Signup register hold timed out';
      const source = isSubmitTimeout
        ? 'signup-register-submit-timeout'
        : 'signup-register-hold-timeout';
      if (
        timeoutType === 'submit-email' ||
        (timeoutType === 'hold' && shouldHoldEmailRegister)
      ) {
        timedOutSignupSubmitRef.current = { intent: 'email' };
      } else if (
        (timeoutType === 'submit-phone' ||
          (timeoutType === 'hold' && shouldHoldSmsRegister)) &&
        pendingSmsPhone
      ) {
        timedOutSignupSubmitRef.current = {
          intent: 'phone',
          phone: {
            phone: pendingSmsPhone,
            country: pendingSmsCountry,
          },
        };
      }
      setAutoSubmitPendingSmsPhone(false);
      setPhoneSubmitInFlight(false);
      setAutoSubmitPendingSignupEmail(false);
      reportClientError(message, new Error(message), {
        flowId: UNIVERSAL_LOGIN_FLOW_ID,
        screenName: currentScreenName,
        source,
      });
      setState(prevState => ({
        ...prevState,
        error: {
          ...prevState.error,
          text: i18n.t('Something went wrong'),
        },
      }));
    },
    [
      currentScreenName,
      pendingSmsCountry,
      pendingSmsPhone,
      shouldHoldEmailRegister,
      shouldHoldSmsRegister,
      setAutoSubmitPendingSignupEmail,
      setAutoSubmitPendingSmsPhone,
      setPhoneSubmitInFlight,
    ]
  );
  useEffect(() => {
    const isSmsHoldLoading = isRegisterHoldLoading(
      shouldHoldSmsRegister && phoneSubmitInFlight,
      state.error?.text
    );
    const isEmailHoldLoading = isRegisterHoldLoading(
      shouldHoldEmailRegister && autoSubmitPendingSignupEmail,
      state.error?.text
    );
    if (!isSmsHoldLoading && !isEmailHoldLoading) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      releaseSignupHold();
    }, 15000);

    return () => window.clearTimeout(timeoutId);
  }, [
    autoSubmitPendingSignupEmail,
    phoneSubmitInFlight,
    releaseSignupHold,
    shouldHoldEmailRegister,
    shouldHoldSmsRegister,
    state.error?.text,
  ]);
  const restartSignupFlow = () => {
    timedOutSignupSubmitRef.current = undefined;
    const hash = window.location.hash;
    const hashQuery = hash.match(/\?.*$/)?.[0] ?? '';
    const currentRoute = hash.match(/^#\/([^?]+)/)?.[1];
    const route = currentRoute === 'claim' ? 'claim' : 'register';
    if (currentRoute !== route) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}#/${route}${hashQuery}`
      );
    }
    latestScreenRef.current = undefined;
    latestNextRef.current = undefined;
    lastReportedScreenNameRef.current = undefined;
    previousReportedScreenNameRef.current = undefined;
    lastReportedSignupArkoseGateRef.current = false;
    previousReportedSignupArkoseGateRef.current = false;
    resetSignupLegState();
    setForm(omitSmsPhone(form));
    setState({ error: {} });
    resetFlowTracking();
    setDescopeFlowResetKey(resetKey => resetKey + 1);
  };
  const previousScreenNameRef = useRef(state.screenName);
  useEffect(() => {
    if (previousScreenNameRef.current !== state.screenName) {
      timedOutSignupSubmitRef.current = undefined;
      previousScreenNameRef.current = state.screenName;
    }
  }, [state.screenName]);
  const heldSignupProps = {
    ...signupSharedProps,
    initialPhone: pendingSmsPhone,
    initialPhoneCountry: pendingSmsCountry,
    initialPhoneSheetOpen: shouldRenderSmsRegister,
    onRegisterHoldTimeout: releaseSignupHold,
    initialLoading: isRegisterHoldLoading(
      shouldHoldSmsRegister && phoneSubmitInFlight,
      state.error?.text
    ),
    errorText: state.error?.text,
    publishViewEvents: false,
    smsSignupDeliveryError:
      smsAuthFlow === 'signup' &&
      state.screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN &&
      isSmsSignupDeliveryFallback(state.error?.text),
  };
  const optimisticSignupProps = useOptimisticSignupProps(
    showOptimisticSignup,
    signupSharedProps,
    flowReadyPromiseRef.current ?? Promise.resolve(),
    () =>
      latestScreenRef.current === ScreenNames.SIGNUP_PAGE
        ? latestNextRef.current
        : undefined,
    handler => {
      const error = new Error('Optimistic signup action timed out');
      reportClientError('Optimistic signup action timed out', error, {
        flowId: UNIVERSAL_LOGIN_FLOW_ID,
        handler,
        screenName: ScreenNames.SIGNUP_PAGE,
        source: 'optimistic-signup-action',
      });
      setState(prevState => ({
        ...prevState,
        error: {
          text: i18n.t('Something went wrong'),
        },
      }));
    }
  );
  useResolveFlowReady(
    state.screenName === ScreenNames.SIGNUP_PAGE,
    resolveFlowReadyRef.current
  );

  return renderAuthScreenTransitionBoundary(
    isScreenTransitionPending,
    <Descope
      key={descopeFlowResetKey}
      flowId={UNIVERSAL_LOGIN_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(formWithoutSmsAuthFlow)}
      onScreenUpdate={(
        screenName: string,
        context: UniversalLoginFlowState,
        next: DescopeSdkNext
      ) => {
        const previousScreenName = lastReportedScreenNameRef.current;
        // smsAuthFlowRef and getHasPendingSignupEmail are callback-time values;
        // arkoseGateForm is only a fallback for unambiguous non-pending gates,
        // so the closure snapshot cannot affect a pending signup decision.
        const currentSignupArkoseGate = isSignupArkoseGate(
          screenName,
          context,
          arkoseGateForm,
          smsAuthFlowRef.current,
          getHasPendingSignupEmail()
        );
        if (screenName !== lastReportedScreenNameRef.current) {
          previousReportedScreenNameRef.current =
            lastReportedScreenNameRef.current;
          previousReportedSignupArkoseGateRef.current =
            lastReportedSignupArkoseGateRef.current;
          lastReportedScreenNameRef.current = screenName;
          lastReportedSignupArkoseGateRef.current = currentSignupArkoseGate;
        }
        setClientErrorContext({
          flowId: UNIVERSAL_LOGIN_FLOW_ID,
          screenName,
        });

        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));
        latestScreenRef.current = screenName;
        latestNextRef.current = nextWithClientSessionId;
        if (
          screenName !== ScreenNames.SIGNUP_PAGE &&
          replaySignupActionRef.current
        ) {
          replaySignupActionRef.current = undefined;
        }
        registerHold.onScreenUpdate(context?.error?.text, {
          isPhoneScreen:
            screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN,
          isEmailOtpScreen: isEmailOtpVerificationScreen(screenName),
          isArkoseScreen: currentSignupArkoseGate,
          isSignupPage: screenName === ScreenNames.SIGNUP_PAGE,
          isReturningToSignupPage:
            screenName === ScreenNames.SIGNUP_PAGE &&
            // This handler is recreated with each screen-state render.
            isSignupContinuationScreen(
              previousScreenName,
              getHasPendingSignupEmail(),
              smsAuthFlowRef.current,
              undefined,
              undefined,
              previousReportedSignupArkoseGateRef.current
            ),
        });
        // Initial-route OTP challenge failures are observable here before the
        // verification screen mounts: Descope returns the structured
        // OTPSignUpOrInEmailFailed type in context.error. That identifies the
        // email OTP challenge path without using the raw recipient, OTP code,
        // or provider-specific error copy. onError does not carry the target
        // screen/channel context consistently enough for this OTP event.
        if (
          !hasSeenOtpVerificationScreenRef.current &&
          isEmailOtpChallengeFailure(context?.error)
        ) {
          const authFlow = getPreVerificationOtpFailureAuthFlow(
            screenName,
            context,
            form
          );
          const otpChannel: AuthOtpChannel = 'email';

          if (!skipNextInitialOtpChallengeEventsRef.current && authFlow) {
            const failureSignature = getInitialOtpFailureSignature(
              authFlow,
              otpChannel,
              screenName,
              context.error
            );

            if (
              failureSignature !==
              lastInitialOtpChallengeFailureSignatureRef.current
            ) {
              lastInitialOtpChallengeFailureSignatureRef.current =
                failureSignature;
              publishAuthOtpChallengeRequestCompletedEvent(
                authFlow,
                'initial_route',
                'failed',
                getAuthOtpChallengeRequestFailureReasonCode(context.error),
                otpChannel
              );
            }
          }
        }

        if (
          isEmailOtpVerificationScreen(screenName) ||
          screenName === PhoneSmsOtpAuthScreenNames.VERIFY_SMS_OTP
        ) {
          hasSeenOtpVerificationScreenRef.current = true;
          resetInitialOtpChallengeFailureReporting();
        } else if (
          screenName === ScreenNames.LOGIN_PAGE ||
          screenName === ScreenNames.SIGNUP_PAGE ||
          screenName === ScreenNames.ARKOSE_LOGIN_GATE ||
          screenName === ScreenNames.ARKOSE_SIGNUP_GATE ||
          screenName === ScreenNames.ARKOSE_SMS_LOGIN_GATE ||
          screenName === ScreenNames.ARKOSE_SMS_SIGNUP_GATE ||
          isArkoseGateVerifyOrBypassScreen(screenName) ||
          screenName === ScreenNames.PASSWORD_OTP_LOGIN ||
          screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN ||
          screenName === ScreenNames.CONFIRM_EMAIL
        ) {
          hasSeenOtpVerificationScreenRef.current = false;
        }

        if (isPotentiallyDisabledUserError(context?.error)) {
          const loginId = extractLoginId(context, form);
          if (loginId && !suspensionCheckedRef.current.has(loginId)) {
            suspensionCheckedRef.current.add(loginId);
            void redirectIfSuspended(loginId, LINKTREE_BASE_URL);
          }
        }

        if (
          (screenName === ScreenNames.LOGIN_PAGE ||
            screenName === ScreenNames.SIGNUP_PAGE) &&
          isOAuthCanceledByUserError(context?.error)
        ) {
          startScreenTransition(() => {
            setState(prevState => ({
              ...prevState,
              ...context,
              next: nextWithClientSessionId,
              screenName,
              error: {},
            }));
          });

          return isUniversalLoginCustomScreen(screenName);
        }

        // Keep the current screen visible if the next screen's chunk is still
        // downloading instead of briefly falling through to a blank fallback.
        startScreenTransition(() => {
          setState(prevState => ({
            ...prevState,
            ...context,
            next: nextWithClientSessionId,
            screenName,
            error: context?.error || {},
          }));
        });

        return isUniversalLoginCustomScreen(screenName);
      }}
      onSuccess={() => {
        setState(prevState => ({ ...prevState }));
        // The redirect to monolith should automatically happen, this is a fallback for when it doesn't.
        if (!isMobileAppSession) {
          if (whitelistedRedirectQueryParams.toString()) {
            const loginUrl = new URL('/login', LINKTREE_BASE_URL);
            loginUrl.searchParams.set('redirect_path', '/admin');
            whitelistedRedirectQueryParams.forEach((value, key) => {
              loginUrl.searchParams.append(key, value);
            });
            window.location.href = loginUrl.toString();
          } else {
            window.location.href = `${LINKTREE_BASE_URL}/login`;
          }
        }
      }}
      onError={(e: unknown) => {
        const errorRecord = isRecord(e) ? e : undefined;
        const errorDetail = errorRecord?.detail ?? e;
        if (isOAuthCanceledByUserError(e)) {
          setState(prevState => ({
            ...prevState,
            error: {},
          }));

          return;
        }

        // TRU-3217: OAuth disabled-user errors surface here (not onScreenUpdate)
        // — Descope bounces back to the Login page and clears context.error, so
        // the screen-update path can't see them. The error string carries
        // E023001 verbatim, so the detection here is unambiguous. Prefer the
        // monolith disambiguation when we have a loginId (e.g. lastAuth got
        // populated from a prior identification step), otherwise redirect
        // directly — OAuth visitors never typed an identifier, so the monolith
        // would have nothing to look up.
        reportClientError('Descope Flow Error', e, {
          flowId: UNIVERSAL_LOGIN_FLOW_ID,
          screenName: state.screenName,
          source: 'descope-on-error',
          type: errorRecord?.type,
        });

        if (
          isConfirmedDisabledError(
            isRecord(errorDetail) ? errorDetail : undefined
          )
        ) {
          const loginId = extractLoginId(state, form);
          if (loginId && !suspensionCheckedRef.current.has(loginId)) {
            suspensionCheckedRef.current.add(loginId);
            void redirectIfSuspended(loginId, LINKTREE_BASE_URL);
          } else if (!loginId) {
            redirectToSuspended(LINKTREE_BASE_URL);
          }
        }

        setState(prevState => ({
          ...prevState,
          error: toDescopeScreenError(errorDetail),
        }));
      }}
    >
      {isProtectedEmailConfirmationScreen(state?.screenName) && (
        <ProtectedEmailConfirmationScreen
          maskedEmail={getProtectedMaskedEmailFromContext(state)}
          {...getProtectedEmailCooldownFromContext(state)}
          onClickConfirm={async (email: string) => {
            if (state.next) {
              resetDescopeError();
              const nextForm = {
                ...(isRecord(state?.form) ? state.form : {}),
                ...(isRecord(form) ? form : {}),
                email,
              };
              setForm(nextForm);
              await state.next(
                PROTECTED_EMAIL_CONFIRMATION_SUBMIT_INTERACTION_ID,
                nextForm
              );
            }
          }}
          onClickUseDifferentLogin={() => {
            window.location.href = `${LINKTREE_BASE_URL}/login`;
          }}
          errorText={displayErrorText}
          rawErrorText={state?.error?.text}
        />
      )}
      {state?.screenName === ScreenNames.LOGIN_PAGE && (
        <LoginScreen
          onFormUpdate={updateForm}
          onClickContinue={async () => {
            if (state.next) {
              resetInitialOtpChallengeFailureReporting();
              resetSignupLegState();
              await state.next(
                'email-otp-login',
                withValidationPurpose(
                  formWithoutSmsAuthFlow,
                  AUTH_IDENTIFIER_CONTINUE_VALIDATION_PURPOSE
                )
              );
            }
          }}
          onClickSMS={async () => {
            if (state.next) {
              const nextForm = {
                ...formWithoutSmsAuthFlow,
                [SMS_AUTH_FLOW_FORM_KEY]: 'login',
              };

              resetSignupLegState();
              setForm(formWithoutSmsAuthFlow);
              await state.next('bD1FMXdZPo', nextForm);
            }
          }}
          onClickGoogle={async () => {
            if (state.next) {
              resetSignupLegState();
              await state.next('w37cm8AmxC', { provider: 'google' });
            }
          }}
          onClickApple={async () => {
            if (state.next) {
              resetSignupLegState();
              await state.next('cJiO2sA0HU', { provider: 'apple' });
            }
          }}
          onClickSignUp={async () => {
            if (state.next) {
              await state.next('ACXl0WypGW', form);
            }
          }}
          onClickPasskey={async () => {
            if (state.next) {
              resetSignupLegState();
              // This flow uses discoverable credentials (allowCredentials is
              // empty), so the typed identifier must not scope passkey login.
              await state.next(PASSKEY_INTERACTION_IDS.LOGIN, {});
            }
          }}
          onIdentifierSubmitted={resetEmailOtpLoginAttempt}
          onEmailOtpAttempted={markEmailOtpLoginAttemptPublished}
          showSmsLogin={showSmsLogin}
          showPasskeyLogin={showPasskeyLogin}
          errorText={displayErrorText}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
        />
      )}
      {state?.screenName === ScreenNames.PASSKEY_ENROLLMENT_AFTER_SIGNUP && (
        <PasskeyEnrollmentScreen
          onClickAddPasskey={async () => {
            if (state.next) {
              await state.next(PASSKEY_INTERACTION_IDS.ENROLL, {});
            }
          }}
          onClickSkip={async () => {
            if (state.next) {
              await state.next(PASSKEY_INTERACTION_IDS.SKIP_ENROLLMENT, {});
            }
          }}
          errorText={displayErrorText}
        />
      )}
      <ArkoseGateScreens
        screenName={state?.screenName}
        authError={authError}
        loginHref={`${LINKTREE_BASE_URL}/login`}
        newDesign={newRegisterDesignEnabled}
        next={state.next}
        arkoseGateForm={arkoseGateForm}
        smsAuthForm={smsAuthForm}
        shouldHoldSignup={shouldHoldArkoseRegister}
      />

      {routeArkoseGateVerifyOrBypassScreen({
        screenName: state?.screenName,
        state,
        form: arkoseGateForm,
        smsAuthForm,
        signupBackHref: `${LINKTREE_BASE_URL}${SIGNUP_REDIRECT_PATH}`,
        loginBackHref: `${LINKTREE_BASE_URL}/login`,
        loginHref: `${LINKTREE_BASE_URL}/login`,
        ArkoseGateScreen,
        authError,
        shouldHoldSignup: shouldHoldArkoseRegister,
        isSignupHoldContext: isSignupArkoseScreen,
      })}
      {signupGateState.shouldRender && !shouldHoldEmailRegister && (
        <SignupScreenGate
          isPending={signupGateState.isPending}
          isEnabled={signupGateState.isEnabled}
          newScreen={
            <NewRegisterScreenGate
              props={{
                ...optimisticSignupProps,
                onRegisterHoldTimeout: releaseSignupHold,
                initialEmail: getStringField(
                  (isRecord(state?.form) ? state.form : {}) as Record<
                    string,
                    unknown
                  >,
                  'email'
                ),
                initialEmailSheetOpen: Boolean(pendingSignupEmail),
              }}
            />
          }
          controlScreen={
            <RegisterScreen
              {...signupSharedProps}
              onClickLogin={async () => {
                if (state.next) {
                  resetSignupLegState();
                  await state.next('B5gkpEXlCm', form);
                }
              }}
              prefilledUsername={prefilledUsername}
              showPaidMediaSignupVariant={showPaidMediaSignupVariant}
            />
          }
        />
      )}
      <HeldNewRegisterScreens
        sms={{ show: shouldRenderSmsRegister, props: heldSignupProps }}
        email={{
          show: shouldHoldEmailRegister,
          props: {
            ...signupSharedProps,
            onRegisterHoldTimeout: releaseSignupHold,
            initialEmail: pendingSignupEmail,
            initialEmailSheetOpen: true,
            initialLoading: isRegisterHoldLoading(
              autoSubmitPendingSignupEmail,
              state.error?.text
            ),
            errorText: state.error?.text,
            publishViewEvents: false,
          },
        }}
        arkose={{
          show: shouldHoldArkoseRegister,
          props: {
            ...heldSignupProps,
            initialEmail: pendingSignupEmail,
            initialEmailSheetOpen: Boolean(pendingSignupEmail),
            initialLoading: true,
            initialPhone: pendingSmsPhone,
            initialPhoneSheetOpen: Boolean(pendingSmsPhone),
          },
        }}
      />
      {state?.screenName === ScreenNames.PASSWORD_OTP_LOGIN && (
        <PasswordLoginScreen
          onFormUpdate={updateForm}
          onClickPasswordLogin={async () => {
            if (state.next) {
              await state.next('A0n-0HflX3', form);
            }
          }}
          onClickEmailOTP={async () => {
            if (state.next) {
              await state.next(
                'ytyEctTvDe',
                withValidationPurpose(
                  form,
                  EMAIL_OTP_FALLBACK_VALIDATION_PURPOSE
                )
              );
            }
          }}
          onEmailOtpChallengeRequested={markExplicitOtpChallengeRequested}
          onEmailOtpChallengeRequestFailed={resetExplicitOtpChallengeRequest}
          onClickEditEmailUsername={async () => {
            if (state.next) {
              await state.next('xX0SpghuGd', form);
            }
          }}
          onClickSignUp={async () => {
            if (state.next) {
              await state.next('CCf_I2IX05', form);
            }
          }}
          onEmailOtpAttempted={markEmailOtpLoginAttemptPublished}
          errorText={displayErrorText}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
        />
      )}
      {(isLoginOtpVerification || isSignupOtpVerification) && (
        <OtpVerificationScreen
          authFlow={isSignupOtpVerification ? 'signup' : 'login'}
          variant={isLoginOtpVerification ? 'banner' : undefined}
          bannerImageDesktop={
            isLoginOtpVerification ? bannerLoginImage : undefined
          }
          hideLogo={isLoginOtpVerification ? false : undefined}
          consumeInitialOtpChallengeRequestTrigger={
            consumeInitialOtpChallengeRequestTrigger
          }
          onFormUpdate={updateForm}
          onOTPVerificationClick={async (code: string) => {
            if (state.next) {
              await state.next('oneTimeCodeId', { ...form, code });
            }
          }}
          onClickResend={async () => {
            if (state.next) {
              await state.next('resend', {});
            }
          }}
          onLoginOtpRouteKnown={
            isLoginOtpVerification ? publishEmailOtpLoginAttemptOnce : undefined
          }
          onBackButtonClick={async () => {
            if (state.next) {
              const isLoginOTP = isLoginOtpVerificationScreen(state.screenName);
              await state.next(isLoginOTP ? 'sd-XX997C-' : '_3P5G3HnMB', form);
            }
          }}
          errorText={displayErrorText}
          rawErrorText={state?.error?.text}
          context={state}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          returningUser={isReturningSignupOtp}
        />
      )}
      {routePhoneSmsOtpAuthScreen({
        screenName: state?.screenName,
        state,
        authFlow: smsAuthFlow,
        formWithoutSmsAuthFlow,
        smsAuthForm,
        smsScreenContext,
        pendingPhone: smsAuthFlow === 'signup' ? pendingSmsPhone : undefined,
        onPendingPhoneUpdate: setPendingSmsPhone,
        onAutoSubmitPendingPhoneUpdate: setAutoSubmitPendingSmsPhone,
        autoSubmitPendingPhone: autoSubmitPendingSmsPhone,
        newDesign: newRegisterDesignEnabled,
        smsAuthAllowedCountries: [...SMS_AUTH_ALLOWED_COUNTRIES],
        onFormUpdate: setForm,
        onPhoneUpdate: setSmsPhone,
        onPhoneLoginBack: resetMobileSmsLoginFlow,
        resetError: resetDescopeError,
        consumeInitialOtpChallengeRequestTrigger,
        PhoneLoginScreen,
        OtpVerificationScreen: OTPVerificationScreen,
        errorText: displayErrorText,
      })}
      {routeMfaChallengeScreen({
        screenName: state?.screenName,
        state,
        form: formWithoutSmsAuthFlow,
        onFormUpdate: setForm,
        resetError: resetDescopeError,
        consumeInitialOtpChallengeRequestTrigger:
          consumeInitialOtpChallengeRequestTrigger,
        OtpVerificationScreen: OTPVerificationScreen,
        errorText: displayErrorText,
      })}
      {state?.screenName === ScreenNames.CHOOSE_USERNAME_EMAIL_OTP && (
        <UsernameScreenComponent
          onClick={async () => {
            if (state.next) {
              await state.next('RgRKIeo9Mb', form);
            }
          }}
          onChange={(data: DescopeFormData) =>
            setForm(prev => ({ ...prev, ...data }))
          }
          onBackButtonClick={async () => {
            if (state.next) {
              await state.next('-YYOF7o-bI', form);
            }
          }}
          onValidateUsername={async () => {
            if (state.next) {
              await state.next('Tyk61-L5EC', form);
            }
          }}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          errorText={displayErrorText}
          context={state}
          prefilledUsername={prefilledUsername}
        />
      )}
      {state?.screenName === ScreenNames.SOCIAL_SIGNUP_USERNAME && (
        <UsernameScreenComponent
          onClick={async () => {
            if (state.next) {
              await state.next('e4kL6KNRMP', form);
            }
          }}
          onChange={(data: DescopeFormData) =>
            setForm(prev => ({ ...prev, ...data }))
          }
          onValidateUsername={async () => {
            if (state.next) {
              await state.next('pZG7UDjKAH', form);
            }
          }}
          resetError={() =>
            setState(prevState => ({
              ...prevState,
              error: {},
            }))
          }
          errorText={displayErrorText}
          isSocialUsername={true}
          context={state}
          prefilledUsername={prefilledUsername}
        />
      )}
    </Descope>
  );
};

const LocalizedDescopeApp: React.FC = () => {
  useLocalizationModeSync();
  const [initialRoute] = useState(() => ({
    flowId: getInitialFlowId(),
    resetPasswordToken: getResetPasswordTokenFromRoute(),
  }));
  const { flowId, resetPasswordToken } = initialRoute;

  useEffect(() => {
    setClientErrorContext({ flowId });
  }, [flowId]);

  return (
    <div className="auth-tw">
      {/* This stays outside both our route Suspense and Descope's internal
          null fallback, so neither lazy boundary can reintroduce a blank gap. */}
      <AuthLoadingShell />
      <React.Suspense fallback={null}>
        {flowId === UNIVERSAL_LOGIN_FLOW_ID ? (
          <UniversalLoginFlow />
        ) : flowId === ADD_MFA_WITH_SMS_OTP_FLOW_ID ? (
          <AddMfaWithSmsOtpFlow />
        ) : flowId === ADD_MFA_WITH_TOTP_FLOW_ID ? (
          <AddMfaWithTotpFlow />
        ) : flowId === RESET_SMS_MFA_FLOW_ID ? (
          <ResetSmsMfaFlow />
        ) : flowId === RESET_TOTP_MFA_FLOW_ID ? (
          <ResetTotpMfaFlow />
        ) : flowId === GUEST_PROMOTION_FLOW_ID ? (
          <GuestPromotionFlow />
        ) : flowId === MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID ? (
          <MobileConsumerPromotionSetEmailFlow />
        ) : flowId === FORGOT_USERNAME_FLOW_ID ? (
          <ForgotUsernameFlow />
        ) : flowId === REQUEST_PASSWORD_RESET_FLOW_ID ? (
          <RequestPasswordResetFlow />
        ) : flowId === RESET_PASSWORD_FLOW_ID ? (
          <ResetPasswordFlow confirmationToken={resetPasswordToken} />
        ) : (
          <HostedDescopeFlow flowId={flowId} />
        )}
      </React.Suspense>
    </div>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    document.documentElement.classList.add('auth-tw');

    // Check if this is a page refresh and redirect to appropriate URL
    const navigationEntries = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    const isPageRefresh =
      navigationEntries.length > 0 && navigationEntries[0]?.type === 'reload';

    if (
      isPageRefresh &&
      !isForgotUsernameRoute() &&
      !isGuestPromotionRoute() &&
      !isRequestPasswordResetRoute() &&
      !isResetPasswordRoute()
    ) {
      window.location.href = `${LINKTREE_BASE_URL}/login`;
    }

    return () => {
      document.documentElement.classList.remove('auth-tw');
    };
  }, []);

  return (
    <AuthProvider
      projectId={DESCOPE_PROJECT_ID}
      baseUrl={DESCOPE_BASE_URL}
      baseStaticUrl={DESCOPE_BASE_URL + '/pages'}
    >
      <StatsigDualInstanceProvider
        sdkKey={STATSIG_SDK_KEY}
        stage={getStatsigStage()}
        user={{}}
        userWarehouseNative={{
          locale: getCompoundLocale(detectStatsigLocale()),
          customIDs: {
            // In mobile WebView sessions, evaluate gates under the native
            // device stableID (forwarded as the mobile_stable_id hash param)
            // so stableID-targeted overrides match the same identity as the
            // native app. Falls back to the browser id on the web.
            stableID: getMobileStableId() ?? getBrowserId(),
          },
        }}
        service="auth0"
        waitForInitialization={false}
      >
        <LocalizedDescopeApp />
      </StatsigDualInstanceProvider>
    </AuthProvider>
  );
};

export default App;
